/**
 * quoteForm.ts
 *
 * Browser-side helpers for the customer-facing form wizard. These call a
 * same-origin proxy route (not Studio directly) so the Studio API key stays
 * server-side — see studioApi.ts for the server-side client that these
 * proxy routes are expected to call.
 *
 * Only the generic, Studio-facing helpers live here. Product/tenant-specific
 * logic (a third-party quoting integration, a legacy submit path) stays in
 * the consumer app.
 */

import type { StudioQuotePreviewResponse } from './studioApi'

interface ApiError {
	detail?: string
	error?: string
	code?: string | number
}

interface ApiSuccess<T = any> {
	success?: boolean
	data?: T
	[key: string]: any
}

/**
 * Strip client-only / computed keys from a payload before sending to Studio.
 *
 * The wizard accumulates non-field data into the form state: the quote preview
 * (`annualPremium`, `paymentFrequency`, `_quotePreview`), the submission result
 * (`_studio`), Stripe (`clientSecret`), and assorted UI context. None of these
 * are Studio field codes, so sending them risks "unknown field" validation
 * failures. Keep only real field values: drop anything starting with `_` and a
 * known blocklist of computed keys.
 */
const COMPUTED_KEYS = new Set([
	'annualPremium',
	'paymentFrequency',
	'basePremium',
	'currency',
	'clientSecret',
	'redirectUrl',
	'prevInputs',
	'quoteId',
])

export function stripInternalKeys(
	data: Record<string, unknown>
): Record<string, unknown> {
	const out: Record<string, unknown> = {}
	for (const [k, v] of Object.entries(data ?? {})) {
		if (k.startsWith('_')) continue
		if (COMPUTED_KEYS.has(k)) continue
		out[k] = v
	}
	return out
}

export interface SubmitFormToStudioOptions {
	formCode: string
	data: Record<string, unknown>
	sourceChannel?: string
	/** Caller's own quote id. Studio persists it on the FormSubmission so a
	 *  later flow (e.g. set-password) can resolve the quote back from a URL
	 *  that only carries the caller's own id. */
	externalReference?: string
	/** Same-origin proxy route that forwards to Studio's submit endpoint.
	 *  Defaults to the crosure-lite-studio convention; override if your app
	 *  names its proxy route differently. */
	endpoint?: string
	/** Optional hook to attach a stored referral code, if the app tracks one.
	 *  Not called when the caller's own `data` already sets `referral_code`. */
	getReferralCode?: () => string | null | undefined
}

/**
 * Submit the full accumulated form payload to Studio via the app's own
 * same-origin proxy route.
 *
 * This is the canonical submit path: Studio owns validation, downstream
 * integrations (CRM/quote calc/etc.) and storage. Use this on the FINAL step
 * of a multi-step wizard with the complete `data` object — Studio does not
 * support partial / per-step saves.
 *
 * Returns the FormSubmission record from Studio, or throws on failure.
 */
export async function submitFormToStudio({
	formCode,
	data,
	sourceChannel = 'web',
	externalReference,
	endpoint = '/api/studio-submit',
	getReferralCode,
}: SubmitFormToStudioOptions): Promise<ApiSuccess> {
	const submittedData = stripInternalKeys(data)
	if (!submittedData.referral_code && getReferralCode) {
		const referralCode = getReferralCode()
		if (referralCode) submittedData.referral_code = referralCode
	}

	const res = await fetch(endpoint, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({
			formCode,
			data: submittedData,
			sourceChannel,
			...(externalReference ? { externalReference } : {}),
		}),
	})

	const json = (await res.json().catch(() => ({}))) as ApiError & ApiSuccess

	if (!res.ok) {
		throw {
			name: 'StudioSubmitError',
			message:
				(json as any).detail ||
				(json as any).error ||
				`Studio submit failed (${res.status})`,
			// Studio returns field-level errors under .errors — surface them so the
			// caller can map them back into RJSF if needed.
			fieldErrors: (json as any).errors,
			status: res.status,
		}
	}

	return json
}

export interface CaptureLeadOptions {
	formCode: string
	data: Record<string, unknown>
	reason?: string
	reason_code?: string
	triggered_rule?: string
	source?: string
	/** Same-origin proxy route. Defaults to the crosure-lite-studio convention. */
	endpoint?: string
}

/**
 * Capture a lead (declined/abandoned prospect) in Studio via the app's own
 * same-origin proxy route. Fire-and-forget: resolves regardless of outcome
 * so the journey is never blocked.
 */
export async function captureLead({
	endpoint = '/api/studio-lead',
	...args
}: CaptureLeadOptions): Promise<void> {
	try {
		await fetch(endpoint, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ ...args, data: stripInternalKeys(args.data) }),
		})
	} catch {
		/* best-effort — never block the journey */
	}
}

export interface PreviewStudioQuoteOptions {
	formCode: string
	data: Record<string, unknown>
	/** Same-origin proxy route. Defaults to the crosure-lite-studio convention. */
	endpoint?: string
}

/**
 * Ask the Studio rating engine for a premium preview from a partial payload,
 * via the app's own same-origin proxy route.
 *
 * Used between wizard steps to populate `annualPremium` / `paymentFrequency`
 * on the in-progress quote object so the price card can render before the
 * final submission. Returns `null` on any error so the caller can keep the
 * user moving — the price block is non-critical UI.
 */
export async function previewStudioQuote({
	formCode,
	data,
	endpoint = '/api/studio-quote-preview',
}: PreviewStudioQuoteOptions): Promise<StudioQuotePreviewResponse | null> {
	try {
		const res = await fetch(endpoint, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ formCode, data: stripInternalKeys(data) }),
		})
		if (!res.ok) return null
		return (await res.json()) as StudioQuotePreviewResponse
	} catch {
		return null
	}
}
